// ─── Statistical Analysis Engine ─────────────────────────────────────────────
// Real computed statistics — no hallucination, no invented coefficients.
// Pure JavaScript implementation: OLS regression, correlation, ANOVA,
// Cronbach alpha, VIF, Breusch-Pagan, moderation, mediation.

import { matTranspose, matMul, matInverse, matDiag, tPValue, fPValue, chiSqPValue, normCDF } from './matrixUtils'

// ── Data preparation ──────────────────────────────────────────────────────────

function extractNumeric(rows, colName) {
  return rows
    .map(r => r[colName])
    .filter(v => v != null && !isNaN(+v))
    .map(Number)
}

function extractMatrix(rows, colNames) {
  const complete = rows.filter(r =>
    colNames.every(c => r[c] != null && !isNaN(+r[c]))
  )
  const X = complete.map(r => colNames.map(c => +r[c]))
  return { X, n: complete.length }
}

function mean(arr) { return arr.reduce((s, v) => s + v, 0) / arr.length }

function variance(arr, ddof = 1) {
  const m = mean(arr)
  return arr.reduce((s, v) => s + (v - m) ** 2, 0) / (arr.length - ddof)
}

function std(arr, ddof = 1) { return Math.sqrt(variance(arr, ddof)) }

function pearsonR(x, y) {
  const n = x.length
  const mx = mean(x), my = mean(y)
  const num = x.reduce((s, xi, i) => s + (xi - mx) * (y[i] - my), 0)
  const dx = Math.sqrt(x.reduce((s, xi) => s + (xi - mx) ** 2, 0))
  const dy = Math.sqrt(y.reduce((s, yi) => s + (yi - my) ** 2, 0))
  if (dx * dy === 0) return 0
  return num / (dx * dy)
}

// ── Descriptive Statistics ────────────────────────────────────────────────────

export function computeDescriptiveStats(rows, variables) {
  const result = {}
  for (const v of variables) {
    const vals = extractNumeric(rows, v.name)
    if (vals.length === 0) continue

    const sorted = [...vals].sort((a, b) => a - b)
    const n = vals.length
    const m = mean(vals)
    const s = std(vals)
    const sk = vals.reduce((sum, x) => sum + ((x - m) / (s || 1)) ** 3, 0) / n
    const ku = vals.reduce((sum, x) => sum + ((x - m) / (s || 1)) ** 4, 0) / n - 3

    const q = (p) => {
      const pos = p * (n - 1)
      const lo = Math.floor(pos), hi = Math.ceil(pos)
      return lo === hi ? sorted[lo] : sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo)
    }

    const nMissing = rows.filter(r => r[v.name] == null || isNaN(+r[v.name])).length

    result[v.name] = {
      label: v.label || v.name,
      n,
      n_missing: nMissing,
      mean: round4(m),
      std: round4(s),
      variance: round4(s * s),
      min: round4(sorted[0]),
      q25: round4(q(0.25)),
      median: round4(q(0.5)),
      q75: round4(q(0.75)),
      max: round4(sorted[n - 1]),
      range: round4(sorted[n - 1] - sorted[0]),
      skewness: round4(sk),
      kurtosis: round4(ku),
    }
  }
  return result
}

// ── Correlation Matrix ────────────────────────────────────────────────────────

export function computeCorrelationMatrix(rows, variables) {
  const varNames = variables.map(v => v.name)
  const arrays = {}
  for (const v of variables) arrays[v.name] = extractNumeric(rows, v.name)

  const matrix = {}
  const pvalues = {}

  for (const vn of varNames) {
    matrix[vn] = {}
    pvalues[vn] = {}
    for (const vn2 of varNames) {
      const x = arrays[vn], y = arrays[vn2]
      // Use only rows where both variables have values
      const pairs = rows
        .filter(r => r[vn] != null && !isNaN(+r[vn]) && r[vn2] != null && !isNaN(+r[vn2]))
        .map(r => [+r[vn], +r[vn2]])
      if (pairs.length < 3) { matrix[vn][vn2] = null; pvalues[vn][vn2] = null; continue }
      const xv = pairs.map(p => p[0]), yv = pairs.map(p => p[1])
      const r = pearsonR(xv, yv)
      const n = pairs.length
      const tStat = r * Math.sqrt(n - 2) / Math.sqrt(1 - r * r + 1e-12)
      matrix[vn][vn2] = round4(r)
      pvalues[vn][vn2] = round4(tPValue(tStat, n - 2))
    }
  }

  return { matrix, pvalues, variables: varNames }
}

// ── OLS Regression ────────────────────────────────────────────────────────────

export function computeOLS(rows, dvName, ivNames, label = '') {
  const allCols = [dvName, ...ivNames]
  const complete = rows.filter(r => allCols.every(c => r[c] != null && !isNaN(+r[c])))
  const n = complete.length
  const k = ivNames.length

  if (n < k + 2) throw new Error(`Insufficient observations (n=${n}, k=${k})`)

  const y = complete.map(r => +r[dvName])
  // Design matrix with intercept
  const X = complete.map(r => [1, ...ivNames.map(c => +r[c])])

  const Xt = matTranspose(X)
  const XtX = matMul(Xt, X)
  const XtXinv = matInverse(XtX)
  if (!XtXinv) throw new Error('Matrix is singular — check for perfect multicollinearity')

  const Xty = matVecMul(Xt, y)
  const beta = matVecMul(XtXinv, Xty)

  // Residuals
  const yHat = X.map(row => row.reduce((s, xi, j) => s + xi * beta[j], 0))
  const resid = y.map((yi, i) => yi - yHat[i])

  const yMean = mean(y)
  const TSS = y.reduce((s, yi) => s + (yi - yMean) ** 2, 0)
  const RSS = resid.reduce((s, e) => s + e ** 2, 0)
  const ESS = TSS - RSS

  const rSq = Math.max(0, Math.min(1, TSS > 0 ? ESS / TSS : 0))
  const adjRSq = 1 - (1 - rSq) * (n - 1) / (n - k - 1)
  const MSE = RSS / (n - k - 1)
  const RMSE = Math.sqrt(MSE)

  const F = (ESS / k) / (RSS / (n - k - 1))
  const fP = fPValue(F, k, n - k - 1)

  const seCoef = matDiag(XtXinv).map(v => Math.sqrt(Math.max(0, v * MSE)))
  const colNames = ['const', ...ivNames]

  const coefficients = {}
  colNames.forEach((name, i) => {
    const b = beta[i], se = seCoef[i]
    const t = b / (se || 1e-10)
    const p = tPValue(t, n - k - 1)
    const z = 1.96 // 95% CI
    coefficients[name] = {
      beta: round4(b),
      std_err: round4(se),
      t_stat: round4(t),
      p_value: round4(p),
      ci_lower: round4(b - z * se),
      ci_upper: round4(b + z * se),
      significant: p < 0.05,
      marginal: p >= 0.05 && p < 0.10,
    }
  })

  // VIF for each IV
  const vif = {}
  for (let i = 0; i < k; i++) {
    const targetIV = ivNames[i]
    const otherIVs = ivNames.filter((_, j) => j !== i)
    if (otherIVs.length === 0) { vif[targetIV] = 1.0; continue }
    try {
      const auxResult = computeOLS(rows, targetIV, otherIVs)
      vif[targetIV] = round4(1 / (1 - auxResult.r_squared + 1e-12))
    } catch { vif[targetIV] = null }
  }

  // Breusch-Pagan heteroscedasticity test
  const bpResult = breuschPagan(complete, resid, ivNames)

  // Durbin-Watson statistic
  let dw = 0
  for (let i = 1; i < resid.length; i++) dw += (resid[i] - resid[i - 1]) ** 2
  dw /= RSS || 1

  // Standardized residuals
  const stdResid = resid.map(e => round4(e / (RMSE || 1)))

  return {
    label: label || `OLS: ${dvName} ~ ${ivNames.join(' + ')}`,
    n,
    r_squared: round4(rSq),
    adj_r_squared: round4(adjRSq),
    f_statistic: round4(F),
    f_pvalue: round6(fP),
    aic: round2(n * Math.log(RSS / n) + 2 * (k + 2)),
    bic: round2(n * Math.log(RSS / n) + Math.log(n) * (k + 2)),
    rmse: round4(RMSE),
    coefficients,
    vif,
    breusch_pagan: bpResult,
    durbin_watson: round4(dw),
    dv: dvName,
    ivs: ivNames,
    residuals: stdResid.slice(0, 50), // first 50 for plotting
    fitted: yHat.slice(0, 50).map(v => round4(v)),
    raw_residuals: resid,
  }
}

function matVecMul(A, v) {
  return A.map(row => row.reduce((s, a, j) => s + a * v[j], 0))
}

// ── Breusch-Pagan test ────────────────────────────────────────────────────────

function breuschPagan(rows, residuals, ivNames) {
  try {
    const squaredResid = residuals.map(e => e ** 2)
    const rowsWithResid = rows.map((r, i) => ({ ...r, __sq_resid: squaredResid[i] }))
    const auxResult = computeOLS(rowsWithResid, '__sq_resid', ivNames)
    const LM = auxResult.n * auxResult.r_squared
    const pval = chiSqPValue(LM, ivNames.length)
    return {
      lm_statistic: round4(LM),
      pvalue: round4(pval),
      heteroscedastic: pval < 0.05,
      interpretation: pval < 0.05
        ? 'Significant heteroscedasticity detected. Consider robust standard errors.'
        : 'No significant heteroscedasticity. Homoscedasticity assumption holds.',
    }
  } catch {
    return { lm_statistic: null, pvalue: null, heteroscedastic: null, interpretation: 'Test could not be computed.' }
  }
}

// ── Moderation Analysis ───────────────────────────────────────────────────────

export function computeModeration(rows, dvName, ivName, moderatorName, label = '') {
  const complete = rows.filter(r =>
    [dvName, ivName, moderatorName].every(c => r[c] != null && !isNaN(+r[c]))
  )

  // Mean-center IV and moderator before creating interaction
  const ivVals = complete.map(r => +r[ivName])
  const modVals = complete.map(r => +r[moderatorName])
  const ivMean = mean(ivVals), modMean = mean(modVals)

  const rowsWithInteraction = complete.map((r, i) => ({
    ...r,
    [`${ivName}_c`]: +r[ivName] - ivMean,
    [`${moderatorName}_c`]: +r[moderatorName] - modMean,
    [`${ivName}_x_${moderatorName}`]: (+r[ivName] - ivMean) * (+r[moderatorName] - modMean),
  }))

  const ivC = `${ivName}_c`
  const modC = `${moderatorName}_c`
  const intTerm = `${ivName}_x_${moderatorName}`

  const mainEffects = computeOLS(rowsWithInteraction, dvName, [ivC, modC], 'Step 1: Main effects')
  const fullModel   = computeOLS(rowsWithInteraction, dvName, [ivC, modC, intTerm], 'Step 2: With interaction')

  const deltaR2 = fullModel.r_squared - mainEffects.r_squared
  const interactionCoef = fullModel.coefficients[intTerm]

  return {
    label: label || `Moderation: ${ivName} × ${moderatorName} → ${dvName}`,
    dv: dvName,
    iv: ivName,
    moderator: moderatorName,
    step1: mainEffects,
    step2: fullModel,
    delta_r_squared: round4(deltaR2),
    interaction: interactionCoef,
    moderation_significant: interactionCoef?.significant ?? false,
    interpretation: interactionCoef?.significant
      ? `Significant moderation effect found (β = ${interactionCoef.beta}, p = ${interactionCoef.p_value}). The relationship between ${ivName} and ${dvName} varies significantly by levels of ${moderatorName}.`
      : `No significant moderation effect (β = ${interactionCoef?.beta}, p = ${interactionCoef?.p_value}). The relationship between ${ivName} and ${dvName} does not significantly vary by ${moderatorName}.`,
  }
}

// ── Cronbach Alpha ────────────────────────────────────────────────────────────

export function computeCronbachAlpha(rows, itemNames, label = '') {
  const complete = rows.filter(r =>
    itemNames.every(c => r[c] != null && !isNaN(+r[c]))
  )
  const n = complete.length
  const k = itemNames.length
  if (k < 2 || n < 5) throw new Error('Need ≥2 items and ≥5 observations')

  const data = itemNames.map(name => complete.map(r => +r[name]))
  const totalScores = complete.map((_, i) => itemNames.reduce((s, name) => s + +complete[i][name], 0))

  const itemVars = data.map(col => variance(col))
  const totalVar = variance(totalScores)

  const sumItemVars = itemVars.reduce((s, v) => s + v, 0)
  const alpha = (k / (k - 1)) * (1 - sumItemVars / (totalVar || 1))

  // Item-total correlations
  const itemTotalCorr = {}
  data.forEach((col, i) => {
    const correctedTotal = totalScores.map((t, j) => t - col[j])
    itemTotalCorr[itemNames[i]] = round4(pearsonR(col, correctedTotal))
  })

  // If-deleted alphas
  const alphaIfDeleted = {}
  itemNames.forEach((name, i) => {
    const remaining = itemNames.filter((_, j) => j !== i)
    if (remaining.length < 2) { alphaIfDeleted[name] = null; return }
    try {
      const reduced = computeCronbachAlpha(rows, remaining)
      alphaIfDeleted[name] = reduced.alpha
    } catch { alphaIfDeleted[name] = null }
  })

  return {
    label: label || `Cronbach Alpha: ${itemNames.join(', ')}`,
    alpha: round4(Math.min(1, Math.max(-1, alpha))),
    n_items: k,
    n_obs: n,
    sum_item_variances: round4(sumItemVars),
    total_variance: round4(totalVar),
    item_total_correlations: itemTotalCorr,
    alpha_if_item_deleted: alphaIfDeleted,
    reliability: alpha >= 0.9 ? 'Excellent' : alpha >= 0.8 ? 'Good' : alpha >= 0.7 ? 'Acceptable' : alpha >= 0.6 ? 'Questionable' : 'Poor',
    items: itemNames,
  }
}

// ── One-way ANOVA ─────────────────────────────────────────────────────────────

export function computeOneWayANOVA(rows, dvName, groupName, label = '') {
  const complete = rows.filter(r => r[dvName] != null && !isNaN(+r[dvName]) && r[groupName] != null)
  const groups = {}
  for (const r of complete) {
    const g = String(r[groupName])
    if (!groups[g]) groups[g] = []
    groups[g].push(+r[dvName])
  }

  const groupNames = Object.keys(groups)
  if (groupNames.length < 2) throw new Error('ANOVA requires at least 2 groups')

  const allVals = complete.map(r => +r[dvName])
  const grandMean = mean(allVals)
  const N = allVals.length
  const k = groupNames.length

  // Between-group SS
  const SSB = groupNames.reduce((s, g) => {
    const gm = mean(groups[g])
    return s + groups[g].length * (gm - grandMean) ** 2
  }, 0)

  // Within-group SS
  const SSW = groupNames.reduce((s, g) => {
    const gm = mean(groups[g])
    return s + groups[g].reduce((ss, v) => ss + (v - gm) ** 2, 0)
  }, 0)

  const dfB = k - 1
  const dfW = N - k
  const MSB = SSB / dfB
  const MSW = SSW / dfW
  const F = MSW > 0 ? MSB / MSW : 0
  const p = fPValue(F, dfB, dfW)
  const etaSq = SSB / (SSB + SSW)

  const groupStats = {}
  groupNames.forEach(g => {
    groupStats[g] = { n: groups[g].length, mean: round4(mean(groups[g])), std: round4(std(groups[g])) }
  })

  return {
    label: label || `ANOVA: ${dvName} by ${groupName}`,
    dv: dvName,
    group: groupName,
    n: N,
    k: k,
    f_statistic: round4(F),
    f_pvalue: round6(p),
    significant: p < 0.05,
    df_between: dfB,
    df_within: dfW,
    ss_between: round4(SSB),
    ss_within: round4(SSW),
    ms_between: round4(MSB),
    ms_within: round4(MSW),
    eta_squared: round4(etaSq),
    effect_size: etaSq >= 0.14 ? 'Large' : etaSq >= 0.06 ? 'Medium' : 'Small',
    group_stats: groupStats,
    grand_mean: round4(grandMean),
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function round4(v) { return Math.round(v * 10000) / 10000 }
function round6(v) { return Math.round(v * 1000000) / 1000000 }
function round2(v) { return Math.round(v * 100) / 100 }

// ── Result formatter for Claude prompt injection ───────────────────────────────

/**
 * Format statistical results as a structured block for Claude interpretation.
 * Claude MUST receive this — never raw numbers Claude invented.
 */
export function formatResultsForInterpretation(results) {
  const sections = []

  for (const result of results) {
    if (!result) continue

    if (result.coefficients) {
      // OLS or Moderation result
      sections.push(`=== ${result.label} ===
Model: OLS Regression
N = ${result.n} | R² = ${result.r_squared} | Adj. R² = ${result.adj_r_squared}
F(${result.ivs?.length}, ${result.n - result.ivs?.length - 1}) = ${result.f_statistic}, p = ${result.f_pvalue}
AIC = ${result.aic} | BIC = ${result.bic} | RMSE = ${result.rmse}

Coefficients:
${Object.entries(result.coefficients)
  .filter(([name]) => name !== 'const')
  .map(([name, c]) =>
    `  ${name}: β = ${c.beta}, SE = ${c.std_err}, t = ${c.t_stat}, p = ${c.p_value} [${c.significant ? 'sig.' : c.marginal ? 'marginal' : 'n.s.'}] 95%CI [${c.ci_lower}, ${c.ci_upper}]`
  ).join('\n')}
  Intercept: ${result.coefficients['const']?.beta}

VIF: ${Object.entries(result.vif || {}).map(([k, v]) => `${k}=${v}`).join(', ')}
Breusch-Pagan: LM=${result.breusch_pagan?.lm_statistic}, p=${result.breusch_pagan?.pvalue} (${result.breusch_pagan?.heteroscedastic ? 'heteroscedastic' : 'homoscedastic'})
Durbin-Watson: ${result.durbin_watson}`)
    }

    if (result.alpha !== undefined) {
      // Cronbach alpha
      sections.push(`=== ${result.label} ===
Cronbach's Alpha = ${result.alpha} (${result.reliability})
Items: ${result.items.join(', ')} (k = ${result.n_items}, N = ${result.n_obs})
Item-total correlations: ${Object.entries(result.item_total_correlations).map(([k, v]) => `${k}=${v}`).join(', ')}`)
    }

    if (result.f_statistic !== undefined && result.group) {
      // ANOVA
      sections.push(`=== ${result.label} ===
One-way ANOVA | N = ${result.n} | Groups: ${result.k}
F(${result.df_between}, ${result.df_within}) = ${result.f_statistic}, p = ${result.f_pvalue} [${result.significant ? 'significant' : 'not significant'}]
Eta-squared = ${result.eta_squared} (${result.effect_size} effect)
Group means: ${Object.entries(result.group_stats).map(([g, s]) => `${g}: M=${s.mean}, SD=${s.std}`).join(', ')}`)
    }

    if (result.matrix) {
      // Correlation matrix
      const vars = result.variables
      sections.push(`=== Correlation Matrix ===
Variables: ${vars.join(', ')}
${vars.map(r => vars.map(c => result.matrix[r][c]).join('\t')).join('\n')}
(p-values available; see pvalues matrix)`)
    }
  }

  return sections.join('\n\n')
}
